import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Form } from 'react-bootstrap';
import './HistoricalEventList.css';
import config from '../../services/apiConfig';
import { apiPaths } from '../../services/apiPath';
import moment from 'moment';

const HistoricalEventList = ({ vendorId, weekNumber, startDate, endDate, onLoadRawHistoricalEvents, onLoadRawHistoricalDaySales, onSelectionChange }) => {
  const [lastYear, setLastYear] = useState([]);
  const [last2Years, setLast2Years] = useState([]);
  const [last3Years, setLast3Years] = useState([]);
  const [dailyComparisons, setDailyComparisons] = useState({});
  const [selected, setSelected] = useState({});
  const [excluded, setExcluded] = useState({});
  const allEvents = [...lastYear, ...last2Years, ...last3Years];
  useEffect(() => {
    const fetchHistoricalEvents = async () => {
      try {
        const lastYearStart = moment(startDate).subtract(1, 'year').format('YYYY-MM-DD');
        const lastYearEnd = moment(endDate).subtract(1, 'year').format('YYYY-MM-DD');
        const last2YearStart = moment(startDate).subtract(2, 'year').format('YYYY-MM-DD');
        const last2YearEnd = moment(endDate).subtract(2, 'year').format('YYYY-MM-DD');
        const last3YearStart = moment(startDate).subtract(3, 'year').format('YYYY-MM-DD');
        const last3YearEnd = moment(endDate).subtract(3, 'year').format('YYYY-MM-DD');

        const response = await fetch(config.BASE_URI + apiPaths.adminData, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payload: {
              body: {
                query_type: 'fetch_previous_week_period_data',
                vendor: vendorId,
                start_date: startDate,
                end_date: endDate,
                last_year_start_date: lastYearStart,
                last_year_end_date: lastYearEnd,
                last_2_year_start_date: last2YearStart,
                last_2_year_end_date: last2YearEnd,
                last_3_year_start_date: last3YearStart,
                last_3_year_end_date: last3YearEnd,
              }
            }
          })
        });

        const data = await response.json();
        if (data.success) {
          setLastYear(data.data || []);
          setLast2Years(data.data_last_2_years || []);
          setLast3Years(data.data_last_3_years || []);
          setDailyComparisons(data.daily_comparisons || {});

          // Init toggles
          const initSelected = {};
          [...(data.data || []), ...(data.data_last_2_years || []), ...(data.data_last_3_years || [])].forEach(e => {
            initSelected[e.event_id] = { include: false, exclude: false };
          });
          setSelected(initSelected);

          if (onLoadRawHistoricalEvents) {
            onLoadRawHistoricalEvents({
              lastYear: data.data,
              last2Years: data.data_last_2_years,
              last3Years: data.data_last_3_years,
            });
          }
          if (onLoadRawHistoricalDaySales) {
            onLoadRawHistoricalDaySales(data.daily_comparisons);
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch historical event data:", error);
      }
    };

    fetchHistoricalEvents();
  }, [vendorId, weekNumber, startDate, endDate]);

  const toggleInclude = (eventId) => {
    const updated = { ...selected, [eventId]: { ...selected[eventId], include: !selected[eventId].include } };
    setSelected(updated);
    updateSelection(updated);
  };

  const toggleExclude = (eventId) => {
    const updated = { ...selected, [eventId]: { ...selected[eventId], exclude: !selected[eventId].exclude } };
    setSelected(updated);
    updateSelection(updated);
  };

  const updateSelection = (updated) => {
    const includedEvents = allEvents.filter(e => updated[e.event_id]?.include);
    onSelectionChange(includedEvents);
  };

  const renderYearlyTable = (events) => (
    events.map(event => (
      <tr key={event.event_id}>
        <td>{event.event_name}</td>
        <td>{event.start_date}</td>
        <td>{event.location}</td>
        <td>${event.total_sales?.toFixed(2)}</td>
        <td>${event.payout?.toFixed(2)}</td>
        <td>${event.total_stock_cost_in_dollars?.toFixed(2)}</td>
        <td>${event.total_wage_cost_for_this_event?.toFixed(2)}</td>
        <td>${event.profit_estimate_after_payout_stock_and_labor?.toFixed(2)}</td>
        <td>
          <Form.Check type="checkbox" label="Include" checked={selected[event.event_id]?.include || false} onChange={() => toggleInclude(event.event_id)} />
          <Form.Check type="checkbox" label="Exclude" checked={selected[event.event_id]?.exclude || false} onChange={() => toggleExclude(event.event_id)} />
        </td>
      </tr>
    ))
  );

  return (
    <div className="mt-4">

      {/* -------- Yearly Tables -------- */}
      <h5 className="fw-bold text-secondary"> Yearly Historical Events</h5>
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h6>Last Year</h6>
          <Table striped bordered hover responsive className="table-fixed">
  <thead>
    <tr>
      <th style={{ minWidth: '150px' }}>Event</th>
      <th style={{ minWidth: '120px' }}>Date</th>
      <th style={{ minWidth: '250px' }}>Location</th>
      <th style={{ minWidth: '120px' }}>Sales</th>
      <th style={{ minWidth: '120px' }}>Payout</th>
      <th style={{ minWidth: '120px' }}>Stock</th>
      <th style={{ minWidth: '120px' }}>Wage</th>
      <th style={{ minWidth: '120px' }}>Profit</th>
      <th style={{ minWidth: '160px' }}>Actions</th>
    </tr>
  </thead>
  <tbody>
    {renderYearlyTable(lastYear)}
  </tbody>
</Table>

          <h6>Last 2 Years</h6>
          <Table striped bordered hover responsive className="table-fixed">
  <thead>
    <tr>
      <th style={{ minWidth: '150px' }}>Event</th>
      <th style={{ minWidth: '120px' }}>Date</th>
      <th style={{ minWidth: '250px' }}>Location</th>
      <th style={{ minWidth: '120px' }}>Sales</th>
      <th style={{ minWidth: '120px' }}>Payout</th>
      <th style={{ minWidth: '120px' }}>Stock</th>
      <th style={{ minWidth: '120px' }}>Wage</th>
      <th style={{ minWidth: '120px' }}>Profit</th>
      <th style={{ minWidth: '160px' }}>Actions</th>
    </tr>
  </thead>
  <tbody>
    {renderYearlyTable(last2Years)}
  </tbody>
</Table>

          <h6>Last 3 Years</h6>
          <Table striped bordered hover responsive className="table-fixed">
  <thead>
    <tr>
      <th style={{ minWidth: '150px' }}>Event</th>
      <th style={{ minWidth: '120px' }}>Date</th>
      <th style={{ minWidth: '250px' }}>Location</th>
      <th style={{ minWidth: '120px' }}>Sales</th>
      <th style={{ minWidth: '120px' }}>Payout</th>
      <th style={{ minWidth: '120px' }}>Stock</th>
      <th style={{ minWidth: '120px' }}>Wage</th>
      <th style={{ minWidth: '120px' }}>Profit</th>
      <th style={{ minWidth: '160px' }}>Actions</th>
    </tr>
  </thead>
  <tbody>
    {renderYearlyTable(last3Years)}
  </tbody>
</Table>
        </Card.Body>
      </Card>

      {/* -------- Day-by-Day Grid -------- */}
      <h5 className="fw-bold text-secondary"> Day-by-Day Event History (Last 3 Years)</h5>
      {Object.keys(dailyComparisons).map(day => (
        <Card className="mb-3 shadow-sm" key={day}>
          <Card.Body>
            <h6>{day}</h6>
            <div className="row">
              {["Last Year", "Last 2 Years", "Last 3 Years"].map(period => {
                const periodData = (dailyComparisons[day] || []).filter(e => e.year_group === period);
                return (
                  <div className="col-md-4" key={period}>
                    <h6>{period}</h6>
                    {periodData.length === 0 ? (
                      <>
                        <p>No event on this day</p>
                        <Button variant="warning">Discover other events that day</Button>
                      </>
                    ) : (
                      periodData.map((e, idx) => (
                        <div key={idx} className="mb-3 p-2 border rounded">
                          <strong>{e.event_name}</strong><br />
                          {e.location}<br />
                          {e.start_date}<br />
                          Sales: ${e.total_sales?.toFixed(2)}<br />
                          Stock: ${e.stock_cost?.toFixed(2)}<br />
                          Profit: ${e.profit?.toFixed(2)}<br />
                          <Button variant="warning" size="sm" className="mt-2">Discover other events that day</Button>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default HistoricalEventList;
